import React from 'react';
import { MoldOrder, MoldOrderWithMold } from '../../types/mold';

export interface FieldRendererProps {
    moldOrder: MoldOrder | MoldOrderWithMold;
    value: string;
    fieldName: string;
}

export type FieldRenderer = React.ComponentType<FieldRendererProps>;

// Custom renderer for product parts - displays as chips
export const ProductPartsRenderer: FieldRenderer = ({ moldOrder }) => {
    const productParts = moldOrder.mold?.productParts ?? [];

    if (productParts.length === 0) {
        return (
            <span className="px-2.5 py-1 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 text-gray-600 rounded-full text-xs font-medium">
                ไม่มีรหัสชิ้นงาน
            </span>
        );
    }

    return (
        <>
            {productParts.map((part) => (
                <span
                    key={part.partNumber}
                    className="px-2.5 py-1 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 text-blue-700 rounded-full text-xs font-medium hover:from-blue-100 hover:to-indigo-100 transition-all duration-200"
                >
                    {part.partNumber}
                </span>
            ))}
        </>
    );
};

// Custom renderer for mold code - displays with a special icon
export const MoldCodeRenderer: FieldRenderer = ({ value }) => {
    if (!value) return <></>
    return (
        <span className="px-2.5 py-1 bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 text-purple-700 rounded-full text-xs font-medium flex items-center gap-1">
            {value}
        </span>
    );
};

// Simple text renderer - displays plain text (for titles)
export const SimpleTextRenderer: FieldRenderer = ({ value }) => (
    <span>{value}</span>
);

// Default text renderer - displays value as simple text in a styled container
export const DefaultTextRenderer: FieldRenderer = ({ value }) => (
    <span className="px-2.5 py-1 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 text-blue-700 rounded-full text-xs font-medium">
        {value}
    </span>
);

// Registry of field-specific renderers for subtitle fields (styled)
export const subtitleFieldRenderers: Record<string, FieldRenderer> = {
    productParts: ProductPartsRenderer,
    moldCode: MoldCodeRenderer,
    // Add more custom renderers here as needed
};

// Registry of field-specific renderers for title fields (simple)
export const titleFieldRenderers: Record<string, FieldRenderer> = {
    // Add custom title renderers here if needed
};

// Get the appropriate renderer for a subtitle field
export const getSubtitleFieldRenderer = (fieldName: string): FieldRenderer => {
    return subtitleFieldRenderers[fieldName] || DefaultTextRenderer;
};

// Get the appropriate renderer for a title field
export const getTitleFieldRenderer = (fieldName: string): FieldRenderer => {
    return titleFieldRenderers[fieldName] || SimpleTextRenderer;
};

// Backward compatibility - use subtitle renderer by default
export const getFieldRenderer = (fieldName: string): FieldRenderer => {
    return getSubtitleFieldRenderer(fieldName);
};

// Get the appropriate label for a field
export const getFieldLabel = (fieldName: string): string => {
    const fieldLabels: Record<string, string> = {
        productParts: 'รหัสชิ้นงาน',
        moldCode: 'Mold Code',
        moldName: 'Mold Name',
        sbiId: 'รหัสแม่พิมพ์',
        // Add more field labels as needed
    };

    return fieldLabels[fieldName] || 'ข้อมูล';
};
